// OWNER: FRONTEND-DIALER. Missed calls (owner request, PJ, 2026-10-24: "when someone calls us back, it like pops us on our
// screen and rings. That's really great. I like that. But I also want a feature where, let's say if I was not at my
// computer or I happen to miss that call, I want somewhere I could see all my missed calls so I can know who called at
// what time so I can call them back."). The ringing pop-up is unchanged; this page lists the inbound calls nobody
// answered in the last 30 days, newest first, repeats from one number within 24 hours folded into one row ("called 3
// times", each time on expand). Each row: who called (the business and city, which line it matched; several businesses
// listed; else Unknown number), their number, when (my time, theirs, how long ago), which of our numbers they rang, a
// voicemail they left (played here), our last call to them before it, and Open / Called back / Done. Call back opens the
// lead and dials the number they called from (web/src/dialer/missedCallBack.ts); Mark done takes an optional note. Mine
// for everyone; Everyone for admins. Words: web/src/dialer/missed.ts.
import '../dialer/dialer.css';
import { useEffect, useState } from 'preact/hooks';
import type { MissedCallRow, MissedCallTime, MissedScope } from '../../../src/shared/api-missed';
import { MISSED_CALL_DAYS, MISSED_NOTE_MAX } from '../../../src/shared/api-missed';
import { ApiFailure, errorMessage } from '../api';
import { navigate } from '../app';
import { Empty, ErrorBanner, toast } from '../components/ui';
import { Segmented, Spinner, useAsync, useNow } from '../components/ui-extra';
import { endpoints } from '../dialer/endpoints';
import { fmtPhone } from '../dialer/labels';
import {
  STATUS_CHIP, callBackLabel, callerWords, lastCallWords, myTz, ourLineWords, rangWords, settledWords, timesWords, vmLength, whenWords,
} from '../dialer/missed';
import { callBackMissed, type MissedCallBackResult } from '../dialer/missedCallBack';
import { VoicemailAudio } from '../dialer/VoicemailAudio';
import { refreshMissed, setMissedOpen } from '../missed';
import { useMe } from '../me';

type Show = 'all' | 'open';

export function MissedCalls(_props: { params: Record<string, string> }) {
  const me = useMe();
  const admin = me.role === 'admin';
  const [scope, setScope] = useState<MissedScope>('mine');
  const [show, setShow] = useState<Show>('all');
  const list = useAsync(() => endpoints.missedCalls(scope), [scope]);
  const now = new Date(useNow(30_000));
  const tz = myTz();

  // Fresh every 60 s while the page is on screen; the badge takes Mine's count from the same answer.
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') list.reload(); }, 60_000);
    return () => clearInterval(t);
  }, [list.reload]);
  useEffect(() => { if (list.data && list.data.scope === 'mine') setMissedOpen(list.data.open); }, [list.data]);

  const all = list.data?.rows ?? [];
  const rows = all.filter((r) => show === 'all' || r.status === 'open');
  const open = all.filter((r) => r.status === 'open').length;

  return (
    <div style="max-width:980px">
      <div class="vd-page-head">
        <div>
          <h1 style="margin:0">Missed calls</h1>
          <p class="muted" style="margin:4px 0 0">
            Calls to our numbers nobody picked up, the last {MISSED_CALL_DAYS} days. Call back dials the number they called from, with every calling rule checked.
          </p>
        </div>
        <div class="row" style="gap:8px;flex-wrap:wrap">
          {admin ? <Segmented<MissedScope> value={scope} onChange={setScope} options={[{ value: 'mine', label: 'Mine' }, { value: 'all', label: 'Everyone' }]} /> : null}
          <Segmented value={show} onChange={setShow} options={[{ value: 'all', label: 'All' }, { value: 'open', label: 'Open', count: open }]} />
          <button class="btn btn-sm" onClick={() => { list.reload(); void refreshMissed(); }}>Refresh</button>
        </div>
      </div>
      <ErrorBanner error={list.error ? `Couldn't load missed calls: ${list.error}` : null} />
      {list.loading && !list.data ? <p class="muted"><Spinner /> Loading missed calls…</p> : null}
      {list.data && rows.length === 0 ? (
        <Empty>{all.length && show === 'open' ? 'Nothing open: every missed call was called back or marked done.' : `No missed calls in the last ${MISSED_CALL_DAYS} days.`}</Empty>
      ) : null}
      {rows.length ? (
        <div class="vd-table-wrap">
          {rows.map((r) => (
            <MissedRow key={r.callId} row={r} now={now} tz={tz} everyone={scope === 'all'}
              onChanged={() => { list.reload(); void refreshMissed(); }} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TimeLine(props: { t: MissedCallTime; now: Date; tz: string; theirTz: string | null; everyone: boolean }) {
  const w = whenWords(props.t.at, props.now, { mine: props.tz, theirs: props.theirTz });
  const st = STATUS_CHIP[props.t.status];
  const settled = settledWords(props.t.settled, props.now, props.tz);
  return (
    <li>
      <span>{w.mine}{w.theirs ? ` · ${w.theirs}` : ''} · {w.ago}</span>{' '}
      <span class={st.cls}>{st.label}</span>
      {settled ? <span class="muted"> {settled}</span> : null}
      {props.everyone ? <span class="muted"> · {rangWords(props.t.rang)}</span> : null}
      {props.t.voicemails.map((v) => <span class="muted"> · left a voicemail{vmLength(v.durationSec) ? ` (${vmLength(v.durationSec)})` : ''}</span>)}
    </li>
  );
}

function MissedRow(props: { row: MissedCallRow; now: Date; tz: string; everyone: boolean; onChanged: () => void }) {
  const r = props.row;
  const [busy, setBusy] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<Extract<MissedCallBackResult, { kind: 'problem' | 'opened' }> | null>(null);
  const who = callerWords(r);
  const newest = r.times[0];
  const w = whenWords(newest.at, props.now, { mine: props.tz, theirs: r.theirTz });
  const st = STATUS_CHIP[r.status];
  const times = timesWords(r.times.length);
  const ourLine = ourLineWords(r.ourNumber);
  const last = lastCallWords(r.lastCall, props.now, props.tz);
  const settled = settledWords(r.settled, props.now, props.tz);
  // Every voicemail they left on the row's calls, newest first (each time is listed on expand).
  const vms = r.times.flatMap((t) => t.voicemails.map((v) => ({ v, at: t.at })));

  const callBack = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const out = await callBackMissed(r, navigate);
      if (out.kind === 'problem' || out.kind === 'opened') setProblem(out);
    } catch (e) {
      setProblem({ kind: 'problem', message: `Couldn't call back: ${errorMessage(e)}` });
    } finally {
      setBusy(false);
    }
  };
  const markDone = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await endpoints.missedDone(r.callId, note.trim() || null);
      toast(`Marked done${res.callIds.length > 1 ? ` (${res.callIds.length} calls)` : ''}.`, 'ok');
      setDoneOpen(false);
      setNote('');
      props.onChanged();
    } catch (e) {
      toast(`Couldn't mark it done: ${errorMessage(e)}`, 'bad');
      if (e instanceof ApiFailure && (e.status === 409 || e.status === 404 || e.status === 403)) props.onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class={`vd-missed${r.status === 'open' ? ' vd-missed-open' : ''}`}>
      <div style="min-width:0">
        <div class="row" style="gap:6px;flex-wrap:wrap">
          <span class="vd-missed-who">{who.title}</span>
          <span class={st.cls}>{st.label}</span>
          {times ? <span class="chip chip-warn">{times}</span> : null}
          {r.dnc ? <span class="chip chip-bad">Do not call</span> : null}
        </div>
        <div class="vd-missed-meta">
          {r.fromE164 ? <span class="mono">{fmtPhone(r.fromE164)}</span> : null}
          <span title={new Date(newest.at).toLocaleString()}>{w.mine}</span>
          {w.theirs ? <span>{w.theirs}</span> : null}
          <span>{w.ago}</span>
          {ourLine ? <span title={r.ourNumber ? `${fmtPhone(r.ourNumber.e164)}${r.ourNumber.metro ? ` · ${r.ourNumber.metro}` : ''}` : undefined}>called {ourLine}</span> : null}
          {props.everyone && r.times.length === 1 ? <span>{rangWords(newest.rang)}</span> : null}
        </div>
        {who.line ? <div class="vd-missed-line">{who.line}</div> : null}
        <div class="vd-missed-line">{last ?? "We hadn't called this number before they called."}</div>
        {settled ? <div class="vd-missed-line"><b>{settled}</b></div> : null}
        {vms.map(({ v, at }) => (
          <div class="vd-missed-vm">
            <span class="muted" style="font-size:12px">
              They left a voicemail{vmLength(v.durationSec) ? ` (${vmLength(v.durationSec)})` : ''}{r.times.length > 1 ? ` at ${whenWords(at, props.now, { mine: props.tz, theirs: null }).mine}` : ''}{v.handledAt ? ' · handled' : ''}
            </span>
            <VoicemailAudio id={v.id} />
          </div>
        ))}
        {r.times.length > 1 ? (
          <details class="vd-missed-times">
            <summary>Each time they called ({r.times.length})</summary>
            <ul>{r.times.map((t) => <TimeLine t={t} now={props.now} tz={props.tz} theirTz={r.theirTz} everyone={props.everyone} />)}</ul>
          </details>
        ) : null}
        {problem ? (
          <div class={problem.kind === 'opened' ? 'vd-pad-alert' : 'vd-pad-alert vd-pad-alert-bad'} role="alert" style="margin-top:6px">
            <b>{problem.message}</b>
            {problem.kind === 'problem' && problem.reasons?.length ? <ul>{problem.reasons.map((x) => <li>{x}</li>)}</ul> : null}
          </div>
        ) : null}
        {doneOpen ? (
          <div class="vd-missed-done">
            <input type="text" value={note} maxLength={MISSED_NOTE_MAX} placeholder="Note (optional): e.g. a robocall, already sorted by text"
              aria-label="Note for marking this missed call done" onInput={(e) => setNote(e.currentTarget.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void markDone(); } if (e.key === 'Escape') setDoneOpen(false); }} />
            <button class="btn btn-sm btn-ink" disabled={busy} onClick={() => void markDone()}>Mark done</button>
            <button class="btn btn-sm" disabled={busy} onClick={() => setDoneOpen(false)}>Cancel</button>
          </div>
        ) : null}
      </div>
      <div class="vd-missed-actions">
        {r.callBack ? (
          <button class={`btn btn-sm${r.status === 'open' ? ' btn-primary' : ''}`} disabled={busy} onClick={() => void callBack()}>
            {busy && !doneOpen ? <Spinner /> : null}{callBackLabel(r)}
          </button>
        ) : r.callBackWhy && r.status === 'open' ? <span class="muted" style="font-size:12px;max-width:220px">{r.callBackWhy}</span> : null}
        {r.canMarkDone && !doneOpen ? <button class="btn btn-sm" disabled={busy} onClick={() => setDoneOpen(true)}>Mark done</button> : null}
        {/* Review 2026-10-24: a call that rang someone else is theirs (or an admin's) to mark done; Call back still works. */}
        {!r.canMarkDone && r.status === 'open' && r.markDoneWhy ? <span class="muted" style="font-size:12px;max-width:220px">{r.markDoneWhy}</span> : null}
      </div>
    </div>
  );
}
